import { Dispatch } from '@reduxjs/toolkit';
import { AppThunk } from '../store';
import {
	asyncCallStart,
	asyncCallError,
	asyncCallDone,
	loginSuccess,
	updateSelfSuccess,
	updateProfilePictureSuccess,
} from '../stores/authStore';
import * as UserService from '../../services/user-service';
import {
	ConvertUserDTO,
	UpdateSelfDTO,
	UserDTO,
	UserUpdateDTO,
	UsersFilter,
} from '../models/user';
import { showAlert } from '../stores/alertStore';
import { fetchUsersSuccess, updateUserSuccess } from '../stores/companyStore';

export const createUsersFromGoogleUsers =
	(users: UserDTO[]): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			await UserService.createUsers(users);
			const response = await UserService.fetchUsers();
			dispatch(fetchUsersSuccess(response));
			dispatch(
				showAlert({
					message: 'All users were successfully created.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error creating users:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to create users. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const updateUser =
	(user: UserUpdateDTO): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const updatedUser = await UserService.updateUser(user);
			dispatch(updateUserSuccess(updatedUser));
			dispatch(
				showAlert({
					message: 'Successfully updated user.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error(error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to update user. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const fetchSelf = (): AppThunk => async (dispatch: Dispatch) => {
	dispatch(asyncCallStart());
	try {
		const user = await UserService.fetchSelf();
		dispatch(updateSelfSuccess({ user }));
	} catch (error) {
		console.error(error);
		dispatch(asyncCallError(error));
	}
	dispatch(asyncCallDone());
};

export const updateSelf =
	(user: UpdateSelfDTO, showSuccessMessage: boolean = true): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const updatedUser = await UserService.updateSelf(user);
			dispatch(updateSelfSuccess({ user: updatedUser }));
		} catch (error) {
			console.error(error);
			dispatch(asyncCallError(error));
		}
		dispatch(asyncCallDone());
		if (showSuccessMessage) {
			dispatch(
				showAlert({
					message: 'Successfully updated your account.',
					severity: 'success',
				})
			);
		}
	};

export const fetchUsers =
	(filter: UsersFilter): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const response = await UserService.fetchUsers(filter);
			dispatch(fetchUsersSuccess(response));
		} catch (error) {
			dispatch(asyncCallError(error));
		}
		dispatch(asyncCallDone());
	};

export const login =
	// (googleCredentials: {
	// 	idToken: string;
	// 	accessToken: string;
	// 	refreshToken: string;
	// }): AppThunk =>


		(credential: string): AppThunk =>
		async (dispatch: Dispatch) => {
			dispatch(asyncCallStart());
			try {
				const response = await UserService.login(credential);
				dispatch(
					loginSuccess({
						user: response.user,
						// credential,
						astroToken: response.astroToken,
					})
				);
			} catch (error) {
				console.error(error);
				dispatch(asyncCallError(error));
				// dispatch(
				//     showAlert({
				//         message: 'An error occurred while fetching your report.',
				//         variant: 'error',
				//     }),
				// );
			}
			dispatch(asyncCallDone());
		};

export const signup =
	(credential: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const response = await UserService.signup(credential);
			dispatch(
				loginSuccess({
					user: response.user,
					astroToken: response.astroToken,
				})
			);
		} catch (error) {
			console.error(error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'An error occurred during signup.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const fetchAndUpdateProfilePicture =
	(): AppThunk => async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const response = await UserService.updateProfilePicture();
			dispatch(
				updateProfilePictureSuccess({
					googleProfilePictureUrl: response,
				})
			);
		} catch (error) {
			console.error(error);
			dispatch(asyncCallError(error));
		}
		dispatch(asyncCallDone());
	};

export const convertExternalUser =
	(user: ConvertUserDTO): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			await UserService.convertExternalUser(user);
			dispatch(
				showAlert({
					message: 'Successfully converted user.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error(error);
			dispatch(asyncCallError(error));
		}
		dispatch(asyncCallDone());
	};

export const sendInvite =
	(userId: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			await UserService.sendInvite(userId);
			dispatch(
				showAlert({
					message: 'Successfully sent invite.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error sending invite:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to send invite. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};
